import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth.store';
import { UserOrganisation } from '@/types/api/auth.types';
import { useLoaderStore } from '@/stores/utils/loader.store';
import { BOQ, BOQItem, BOQItemGroup, BOQSection, BOQStatus } from '@/types/api/sites.types';

import i18n from '@/plugins/vue-i18n';
import { Status } from '@/common/constants';
import { Review, ReviewStage } from '@/types/api/organisations.types';
import { useToastStore } from '@/stores/utils/toast.store';

import { Tag } from '@boqnet/ui';

type TagProps = InstanceType<typeof Tag>['$props'];

export type StatusTagConfig = {
  label: string;
  color?: TagProps['color'];
  class?: string;
  isOutline?: boolean;
};

const { t } = i18n.global;

/**
 * Finds the `PushBack`-stage review on a payment request, if any.
 * A push-back is recorded as a review with `stage === ReviewStage.PushBack`;
 * the reason lives in that review's `notes`.
 */
export const getPushBackReview = (reviews?: Review[] | null): Review | undefined =>
  reviews?.find((review) => review.stage === ReviewStage.PushBack);

/**
 * A payment request is "pushed back" when it is still `Requested` and carries a
 * `PushBack` review. This is a derived state — there is no `PushedBack` status.
 * Shared by the list badge, the filter, and the drawer banner so the definition
 * cannot drift between surfaces.
 */
export const isPushedBack = (
  pr?: { status?: Status | string | null; reviews?: Review[] | null } | null,
): boolean => pr?.status === Status.requested && Boolean(getPushBackReview(pr?.reviews));

export const viewImage = (url: string) => {
  window.open(url, '_blank', 'noopener');
};

/**
 * Opens the document tab during the tap and points it at the URL once fetched — Safari blocks a
 * `window.open` that only runs after an await as an unrequested pop-up (PLE-1671).
 */
export const viewFetchedDocument = async (fetchUrl: () => Promise<string | null | undefined>) => {
  // The handle is the point here, and `noopener` would return null: `opener` is cleared below.
  // eslint-disable-next-line boq/window-open-noopener -- opener is set to null once the tab is held
  const documentTab = window.open('', '_blank');

  try {
    const url = await fetchUrl();

    if (!url) {
      documentTab?.close();
    } else if (documentTab) {
      documentTab.opener = null;
      documentTab.location.href = url;
    } else {
      viewImage(url);
    }
  } catch (error) {
    documentTab?.close();
    throw error;
  }
};

export const getExtensionFromContentType = (contentType: string): string => {
  const mimeTypes: { [key: string]: string } = {
    'text/plain': '.txt',
    'text/html': '.html',
    'application/json': '.json',
    'application/pdf': '.pdf',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/svg+xml': '.svg',
    'audio/mpeg': '.mp3',
    'audio/wav': '.wav',
    'video/mp4': '.mp4',
    'video/x-msvideo': '.avi',
    'application/zip': '.zip',
    'application/x-rar-compressed': '.rar',
    'application/octet-stream': '.bin', // Generic binary data
    // Add more mappings as needed
  };

  // Return the appropriate extension or a default
  return mimeTypes[contentType] || '.bin';
};

export const updateUserOrgLogo = (orgId: string, logoUrl: string, logoName: string) => {
  const authStore = useAuthStore();
  const { loggedInUser } = storeToRefs(authStore);

  const org = loggedInUser.value?.organisations?.find((org: UserOrganisation) => org.id === orgId);
  if (org) {
    org.logoUrl = logoUrl;
    org.logoName = logoName;
  }

  authStore.setUser(loggedInUser.value);
};

export const exportFile = (file: Blob, type: string, name: string) => {
  const extension = getExtensionFromContentType(type);

  const objectUrl = globalThis.URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${name}${extension}`;
  document.body.append(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(objectUrl);
};

export const getOrdinalSuffix = (n: number): string => {
  if (n < 1 || n > 31) {
    throw new Error('Number must be between 1 and 31.');
  }

  const suffixes = ['th', 'st', 'nd', 'rd'];
  const mod100 = n % 100;
  const mod10 = n % 10;

  if (mod100 >= 11 && mod100 <= 13) {
    return 'th';
  }

  return suffixes[mod10 > 3 || mod10 === 0 ? 0 : mod10] ?? 'th';
};

export const findItemByPathAndName = (
  sections: BOQSection[],
  path: number[],
  name: string,
  searchInItemGroups: boolean,
  boqStatus: BOQStatus,
): BOQItem | null => {
  const findInItemGroups = (section: BOQSection): BOQItem | null => {
    for (const group of section.itemGroups) {
      if (JSON.stringify(group.path) === JSON.stringify(path)) {
        return (
          group.items.find((item) =>
            boqStatus === BOQStatus.approved
              ? item.name === name && item.pendingVariation && !item.pendingVariation.specification
              : item.name === name,
          ) || null
        );
      }
    }
    return null;
  };

  const findInSectionItems = (section: BOQSection): BOQItem | null => {
    if (JSON.stringify(section.path) === JSON.stringify(path)) {
      return (
        section.items.find((item) =>
          boqStatus === BOQStatus.approved
            ? item.name === name && item.pendingVariation && !item.pendingVariation.specification
            : item.name === name,
        ) || null
      );
    }
    return null;
  };

  for (const section of sections) {
    if (searchInItemGroups) {
      const groupItem = findInItemGroups(section);
      if (groupItem) return groupItem;
    }

    const foundItem = findInSectionItems(section);
    if (foundItem) return foundItem;

    const nestedItem = findItemByPathAndName(
      section.sections,
      path,
      name,
      searchInItemGroups,
      boqStatus,
    );
    if (nestedItem) return nestedItem;
  }

  return null;
};

export const findSectionOrGroupByPathAndName = (
  sections: BOQSection[],
  path: number[],
  name: string,
  searchInItemGroups: boolean,
): BOQSection | BOQItemGroup | null => {
  const stringifyPath = (paths: number[]) => JSON.stringify(paths);
  const targetPathString = stringifyPath(path);

  const findInSectionsRecursive = (sectionList: BOQSection[]): BOQSection | null => {
    for (const section of sectionList) {
      // Check if the current section matches by path and name
      if (stringifyPath(section.path) === targetPathString && section.name === name) {
        return section;
      }

      // Recursively search in nested sections
      const foundInNestedSections = findInSectionsRecursive(section.sections);
      if (foundInNestedSections) {
        return foundInNestedSections;
      }
    }
    return null;
  };

  const findInItemGroupsRecursive = (sectionList: BOQSection[]): BOQItemGroup | null => {
    for (const section of sectionList) {
      for (const group of section.itemGroups) {
        // Check if the current item group matches by path and name
        if (stringifyPath(group.path) === targetPathString && group.name === name) {
          return group;
        }
      }

      // Recursively search in item groups within nested sections
      const foundInNestedSectionsGroups = findInItemGroupsRecursive(section.sections);
      if (foundInNestedSectionsGroups) {
        return foundInNestedSectionsGroups;
      }
    }
    return null;
  };

  return searchInItemGroups
    ? findInItemGroupsRecursive(sections)
    : findInSectionsRecursive(sections);
};

export const findItemBySectionAndItemId = (
  sections: BOQSection[],
  sectionId: string,
  itemId: string,
  searchInItemGroups: boolean,
): BOQItem | null => {
  const findInItemGroups = (section: BOQSection): BOQItem | null => {
    for (const group of section.itemGroups) {
      const foundItem = group.items.find((item) => item.id === itemId);
      if (foundItem) return foundItem;
    }
    return null;
  };

  const findInSectionItems = (section: BOQSection): BOQItem | null => {
    return section.items.find((item) => item.id === itemId) || null;
  };

  for (const section of sections) {
    // Check if the current section matches the sectionId
    if (section.id === sectionId) {
      if (searchInItemGroups) {
        const groupItem = findInItemGroups(section);
        if (groupItem) return groupItem;
      }

      const foundItem = findInSectionItems(section);
      if (foundItem) return foundItem;
    }

    // Recursively search in nested sections
    const nestedItem = findItemBySectionAndItemId(
      section.sections,
      sectionId,
      itemId,
      searchInItemGroups,
    );
    if (nestedItem) return nestedItem;
  }

  return null;
};

export const getStatusTagConfig = (status: Status | string): StatusTagConfig => {
  switch (status) {
    case Status.approved: {
      return { label: t('statuses.approved'), color: 'green' };
    }
    case Status.awaitingProof: {
      return { label: t('statuses.awaitingProof'), color: 'orange' };
    }
    case Status.cancelled: {
      return { label: t('statuses.cancelled'), color: 'red' };
    }
    case Status.closed: {
      return { label: t('statuses.closed'), color: 'red' };
    }
    case Status.complete: {
      return { label: t('statuses.complete'), color: 'blue' };
    }
    case Status.credit: {
      return { label: t('statuses.credit'), color: 'blue' };
    }
    case Status.current: {
      return { label: t('statuses.current'), color: 'orange' };
    }
    case Status.declined: {
      return { label: t('statuses.declined'), color: 'red' };
    }
    case Status.delivered: {
      return { label: t('statuses.delivered'), color: 'green' };
    }
    case Status.draft: {
      return { label: t('statuses.draft'), color: 'gray' };
    }
    case Status.expired: {
      return { label: t('statuses.expired'), color: 'red' };
    }
    case Status.live: {
      return { label: t('statuses.live'), color: 'green' };
    }
    case Status.na: {
      return { label: t('statuses.na'), color: 'gray' };
    }
    case Status.paid: {
      return { label: t('statuses.paid'), color: 'blue' };
    }
    case Status.partialDelivered: {
      return { label: t('statuses.partialDelivered'), color: 'orange' };
    }
    case Status.partialPaid: {
      return { label: t('statuses.partialPaid'), color: 'orange' };
    }
    case Status.pending: {
      return { label: t('statuses.pending'), color: 'orange' };
    }
    case Status.planning: {
      return { label: t('statuses.planning'), color: 'gray' };
    }
    case Status.prsPending: {
      return { label: t('statuses.prsPending'), color: 'orange' };
    }
    case Status.rejected: {
      return { label: t('statuses.rejected'), color: 'red' };
    }
    case Status.requested: {
      return { label: t('statuses.requested'), color: 'orange' };
    }
    case Status.returned: {
      return { label: t('statuses.returned'), color: 'gray' };
    }
    case Status.settled: {
      return { label: t('statuses.settled'), color: 'green' };
    }
    case Status.unpaid: {
      return { label: t('statuses.unpaid'), color: 'orange' };
    }
    case Status.verified: {
      return { label: t('statuses.verified'), color: 'violet' };
    }

    default: {
      return { label: '', color: 'gray' };
    }
  }
};

interface WatchLoaderToastOptions {
  responseCode: number;
  title: string;
  description: string;
  showToast?: boolean;
  onSuccess?: () => Promise<void> | void;
  onError?: () => Promise<void> | void;
}

export function documentUploadToast({
  responseCode,
  title,
  description,
  showToast = true,
  onSuccess,
  onError,
}: WatchLoaderToastOptions) {
  const toastStore = useToastStore();
  const loaderStore = useLoaderStore();

  const stop = watch(
    () => loaderStore.getLoaderByName('document-upload'),
    async () => {
      if (!loaderStore.getLoaderByName('document-upload')) {
        try {
          if (responseCode >= 200 && responseCode < 300) {
            if (showToast) {
              toastStore.showToast('success', title, description);
            }
            if (onSuccess) await onSuccess();
          } else if (responseCode === 403) {
            toastStore.showToast(
              'error',
              t('documents.toasts.errorTitle'),
              t('documents.toasts.errorSubtitleFileBlocked'),
            );
            if (onError) await onError();
          } else {
            toastStore.showToast(
              'error',
              t('documents.toasts.errorTitle'),
              t('documents.toasts.errorSubtitleGeneric'),
            );
            if (onError) await onError();
          }
        } finally {
          stop();
        }
      }
    },
  );
}

export const findItemById = (sections: BOQSection[], idToFind: string): BOQItem | null => {
  for (const section of sections) {
    // 1. Check current section's direct items
    const foundInSectionItems = section.items.find((item) => item.id === idToFind);
    if (foundInSectionItems) {
      return foundInSectionItems;
    }

    // 2. Check current section's item groups
    for (const group of section.itemGroups || []) {
      const foundInGroupItems = group.items.find((item) => item.id === idToFind);
      if (foundInGroupItems) {
        return foundInGroupItems;
      }
    }

    // 3. Search recursively in nested sections.
    // If the recursive call finds an item anywhere deeper, return it immediately.
    if (section.sections && section.sections.length > 0) {
      const nestedItem = findItemById(section.sections, idToFind);
      if (nestedItem) {
        return nestedItem;
      }
    }
  }
  return null;
};

/**
 * Updates items in the given BOQ in place by matching and merging properties from itemsToUpdate.
 *
 * @param boq - The BOQ object whose items will be updated. This object is mutated in place.
 * @param itemsToUpdate - An array of BOQItem objects containing updated properties.
 * @returns void
 * @mutates boq
 */
export const updateBOQWithItems = (boq: BOQ, itemsToUpdate: BOQItem[]) => {
  for (const newItem of itemsToUpdate) {
    const itemToUpdate = findItemById(boq.sections, newItem.id);

    if (itemToUpdate) {
      Object.assign(itemToUpdate, newItem);
    } else {
      console.warn(`Item with ID: ${newItem.id} not found in BOQ. Skipping update.`);
    }
  }
};
